import { LocalSessionKey } from 'app/constants/app-constants';
import { VOUCHER_PASSWORD } from 'app/constants/api-contants';
import { LocalStorage } from 'app/utils/storage';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  exportCustomers,
  getCustomerList,
  restoreCustomerStatus,
  updateCustomerStatus,
} from 'app/api/customer';
import { startExportPoll } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const EMPTY_FILTERS = { status: '', lastActiveDate: '' };

const useCustomerManagement = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [values, setValues] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportCustomers({});
      if (response?.responseCode !== 200) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      const jobId = response?.data?.jobId;
      if (!jobId) {
        Toast.error(Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      Toast.success('Export started. Preparing your file...');

      startExportPoll(jobId, {
        onDone: (url) => {
          setIsExporting(false);
          window.location.href = url;
          Toast.success('Export ready. File download started.');
        },
        onFailed: (error) => {
          setIsExporting(false);
          Toast.error(error || Message.Error.COMMON_MESSAGE);
        },
        onError: () => {
          setIsExporting(false);
          Toast.error(Message.Error.COMMON_MESSAGE);
        },
      });
    } catch {
      Toast.error(Message.Error.COMMON_MESSAGE);
      setIsExporting(false);
    }
  };

  const sectionObj = {
    sectionTitle: 'Customer Dashboard',
    searchObj: {
      keys: [''],
      placeholder: 'Search by Name or Contact No.',
      searchType: 'server',
      handleSearch: (e) => {
        console.log('Handle search: ', e);
      },
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: isExporting ? 'Exporting...' : 'Export',
        handleBtnClick: handleExport,
        isDisabled: isExporting,
      },
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen((prev) => !prev),
      },
    ],
  };

  const navigate = useNavigate();
  const location = useLocation();

  const [totalNumber, setTotalNumber] = useState(0);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const [dataPerPage, setDataPerPage] = useState(10);

  const [passwordmodelOpen, setPasswordmodelOpen] = useState(false);
  const [passwordVal, setPasswordVal] = useState('');
  const [tempdata, setTempdata] = useState({});
  const [actionKey, setActionKey] = useState('');
  const [pendingPage, setPendingPage] = useState(1);

  function changeHandler(event) {
    const pageLength = event.target.value;
    setDataPerPage(pageLength);
    let count = Math.ceil(data.length / pageLength);
    setPageCount(count);
  }

  const fetchCustomerData = useCallback(
    async (page, searchValue = '', filters = appliedFilters) => {
      setPageLoading(true);
      let requestParam = {
        page,
        limit: dataPerPage,
        search: searchValue,
        ...(filters.status && { status: filters.status }),
        ...(filters.lastActiveDate && { lastActiveDate: filters.lastActiveDate }),
      };
      try {
        const response = await getCustomerList(requestParam);
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.customers || [];
          setData(dataArr);
          setFilteredData(dataArr);

          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage, appliedFilters]
  );

  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [isFiltering]);

  function handleSubmit(event) {
    event.preventDefault();
    setAppliedFilters(values);
    setIsFilterOpen(false);
    setIsFiltering(true);
  }

  function handleFilterCancel() {
    setIsFilterOpen(false);
  }

  function resetfilter() {
    setValues(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setIsFiltering(true);
  }

  function handleNew(data) {
    LocalStorage.set(LocalSessionKey.CUSTOMER_DATA, data);
    navigate(`/customers/details/${data.id}`, {
      state: {
        ...data,
        // Remember the list's URL (page + search) so the detail's back button
        // returns here with the list state preserved.
        listUrl: `${location.pathname}${location.search}`,
      },
    });
  }

  const _executeStatusChange = async (data, page) => {
    const newStatus = data.status === 3 ? 1 : 3;
    setPageLoading(true);
    const response = await updateCustomerStatus(data?.id, newStatus);
    setPageLoading(false);

    if (response?.responseCode === 200) {
      Toast.success(
        newStatus === 3
          ? Message.Customer.STATUS_BLOCKED_SUCCESS
          : Message.Customer.STATUS_UNBLOCKED_SUCCESS
      );
      fetchCustomerData(page);
    } else {
      Toast.error(
        response?.response?.message || Message.Customer.STATUS_UPDATED_FAILURE
      );
    }
  };

  const _executeRestore = async (data, page) => {
    setPageLoading(true);
    const response = await restoreCustomerStatus(data?.id);
    setPageLoading(false);

    if (response?.responseCode === 200) {
      Toast.success(Message.Customer.STATUS_RESTORED_SUCCESS);
      fetchCustomerData(page);
    } else {
      Toast.error(
        response?.response?.message || Message.Customer.STATUS_UPDATED_FAILURE
      );
    }
  };

  const handleStatusChange = (data, page) => {
    const key = data.status === 3 ? 'unblock' : 'block';
    setTempdata(data);
    setActionKey(key);
    setPendingPage(page);
    setPasswordmodelOpen(true);
  };

  const handledDeleteStatusChange = (data, page) => {
    setTempdata(data);
    setActionKey('restore');
    setPendingPage(page);
    setPasswordmodelOpen(true);
  };

  function handlemodelvalues() {
    setPasswordmodelOpen(false);

    if (passwordVal && passwordVal !== VOUCHER_PASSWORD.VOUCHER_PASSWORD) {
      Toast.error('Your Entered Password is wrong.');
      setPasswordVal('');
      return;
    }
    setPasswordVal('');

    if (actionKey === 'block' || actionKey === 'unblock') {
      _executeStatusChange(tempdata, pendingPage);
    } else if (actionKey === 'restore') {
      _executeRestore(tempdata, pendingPage);
    }
  }

  return {
    handleStatusChange,
    handledDeleteStatusChange,
    handlemodelvalues,
    handleNew,
    handleExport,
    isExporting,
    changeHandler,
    pageLoading,
    filteredData,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    fetchCustomerData,
    passwordmodelOpen,
    setPasswordmodelOpen,
    passwordVal,
    setPasswordVal,
    isFilterOpen,
    setIsFilterOpen,
    isFiltering,
    values,
    setValues,
    appliedFilters,
    handleSubmit,
    handleFilterCancel,
    resetfilter,
  };
};

export default useCustomerManagement;
